/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
/* eslint-disable no-empty */
/* global window sessionStorage localStorage document */

const { NullTerm } = require('@cedalo/parser');
const qrjs2 = require('qrjs2');

const JSG = require('../../JSG');
const ItemAttributes = require('../attr/ItemAttributes');
const Expression = require('../expr/Expression');
const WorksheetNode = require('./WorksheetNode');
const CellsNode = require('./CellsNode');
const NotificationCenter = require('../notifications/NotificationCenter');
const GraphUtils = require('../GraphUtils');
const AddImageCommand = require('../command/AddImageCommand');

const createSelectionId = (user, sessionId) =>
	user && sessionId ? `${sessionId};${user.id};${user.displayName}` : undefined;

/**
 * Node representing a worksheet. The worksheet contains additional nodes for the rows,
 * columns, cells and the top left corner.
 *
 * @class StreamSheet
 * @extends WorksheetNode
 * @constructor
 */
class StreamSheet extends WorksheetNode {
	constructor() {
		super();

		const columns = this.getColumns();
		columns.setSectionSize(0, 0);
		columns.setSectionSize(1, 700);

		const attr = this.getItemAttributes();
		attr.setSelectionMode(ItemAttributes.SelectionMode.NONE);
		attr.setPortMode(ItemAttributes.PortMode.NONE);
		attr.setContainer(false);

		// this._addImageCmds = [];
	}

	newInstance() {
		return new StreamSheet();
	}

	// to prevent cyclic dependencies if StreamSheet is used in commands
	get isStreamSheet() {
		return true;
	}

	// returns the StreamSheet id not the JSG one. To get latter use getId()
	get id() {
		return this.model.id;
	}

	assignIdsToChildren(item, lid) {
		this._contentPane.subItems.forEach((subItem) => {
			subItem._id = lid;
			lid += 1;
			// graph items in sheet keep their id (port etc.)
			if (!(subItem instanceof CellsNode)) {
				lid = subItem.assignIdsToChildren(subItem, lid);
			}
		});

		return lid;
	}

	getItemType() {
		return 'processsheet';
	}

	_assignName(id) {
		if (this.getGraph()._reading) {
			return;
		}
		const attr = this.getAttributeAtPath('range');
		if (attr) {
			this.setName(`Sheet${id}`);
			return;
		}

		const myId = this.getId();
		const graph = this.getGraph();
		const oldName = this.getName().getValue();
		let newId = 1;
		let newName = oldName || 'S1';
		let item = graph.getItemByName(newName);

		while (item && item.getId() !== myId) {
			newId += 1;
			newName = `Sheet${newId}`;
			item = graph.getItemByName(newName);
		}
		this.setName(newName);
	}

	updateFormulas() {
		JSG.streamSheets.forEach(container => {
			const data = container.sheet.cells;
			data.enumerate((column, row, cell) => {
				const expr = cell.getExpression();
				if (expr !== undefined) {
					expr.correctFormula(container.sheet);
				}
			});
		});
	}

	isAddLabelAllowed() {
		return false;
	}

	getStreamSheetContainer() {
		const parent = this.getParent();
		if (!(parent instanceof JSG.StreamSheetContainer)) {
			return undefined;
		}
		return parent;
	}

	setShapes(json) {
		if (!json || !json.shapes) {
			return;
		}

		let notify = false;
		const changed = this.getCells()._shapesChanged;
		if (changed !== undefined && json.changed !== changed) {
			// to prevent, that old updates are processed
			return;
		}

		this.getCells()._shapesChanged = undefined;
		const itemMap = {};
		const deleteMap = {};
		const parentMap = {};
		let parent;

		GraphUtils.traverseItem(this.getCells(), item => {
			if (itemMap[item.getId()]) {
				const dup = itemMap[item.getId()]
				dup.setId(-10)
				dup.getParent().removeItem(dup);
			}
			deleteMap[item.getId()] = item;
			itemMap[item.getId()] = item;
		}, false);

		// read and create items
		json.shapes.forEach(shape => {
			let node = itemMap[shape.id];
			if (shape.sheetShape) {
				parent = parentMap[shape.parent];
				if (parent) {
					shape.parent = parent.getCells().getId();
				}
			}
			if (!node) {
				node = JSG.graphItemFactory.createItemFromString(shape.itemType, true);
				if (!node) {
					return;
				}
				const s = JSG.ShapeFactory.createShapeFromString(shape.shape.type);
				if (s) {
					node.setShapeTo(s);
					s.fromJSON(shape.shape);
				}

				if (shape.parent === undefined || shape.parent === -10) {
					parent = this.getCells();
				} else {
					// TODO: review - is it ok to go with cells as default parent?
					parent = parentMap[shape.parent] || this.getCells();
				}
				// if (!parent) {
				// 	parent = this.getCells().getItemById(shape.parent);
				// }
				if (parent) {
					node.fromJSON(shape);
					parent.addItem(node);
					notify = true;
				}
			} else if (shape.parent !== node.getParent().getId()) {
				parent = this.getCells().getItemById(shape.parent);
				if (parent) {
					node.changeParent(parent);
				}
			}
			const jsonShape = JSON.stringify(shape);
			if (!node._lastJSON || node._lastJSON !== jsonShape) {
				const eventEnabled = node.disableEvents();
				notify = true;
				node.fromJSON(shape);
				node.evaluate();
				node.enableEvents(eventEnabled);
				let pattern = node.getFormat().getPattern().getValue();
				node.getFormat().setPatternFromShape();
				if (pattern) {
					try {
						const qr = pattern.indexOf('qrcode:');
						if (qr !== -1) {
							if (window) {
								const text = pattern.slice(7);
								pattern = window.QRCode.generatePNG(text, {
									ecclevel: 'M',
									format: 'html',
									fillcolor: '#FFFFFF',
									textcolor: '#373737',
									margin: 4,
									modulesize: 8
								});
							}
						}

						const uri = pattern.indexOf('data:image') !== -1;
						if (uri) {
							const id = `dataimage${JSG.imagePool.getNewId()}`;
							// to transfer image to server later on
							// this._addImageCmds.push(new AddImageCommand(id, pattern));
							JSG.imagePool.set(pattern, id);
							node.getFormat().setPatternFromShape(id);
						} else {
							const parts = pattern.split('?', 2);
							if (parts.length > 1) {
								JSG.imagePool.update(parts[0], parts[1]);
								node.getFormat().setPatternFromShape(parts[0]);
							} else {
								node.getFormat().setPatternFromShape(pattern);
							}
						}
					} catch (e) {
					}
				}
                node.setRefreshNeeded(true);
			}
			if (shape.sheetPos) {
				const box = new JSG.BoundingBox();
				const angle = node.getAngle().getValue();
				box._topleft.x = Math.min(this.model.columns.getSectionPos(shape.sheetPos.colLeft + 1),
					this.model.columns.getSectionPos(shape.sheetPos.colLeft) + shape.sheetPos.colLeftOffset);
				box._bottomright.x = Math.min(this.model.columns.getSectionPos(shape.sheetPos.colRight + 1),
					this.model.columns.getSectionPos(shape.sheetPos.colRight) + shape.sheetPos.colRightOffset);
				box._topleft.y = Math.min(this.model.rows.getSectionPos(shape.sheetPos.rowTop + 1),
					this.model.rows.getSectionPos(shape.sheetPos.rowTop) + shape.sheetPos.rowTopOffset);
				box._bottomright.y = Math.min(this.model.rows.getSectionPos(shape.sheetPos.rowBottom + 1),
					this.model.rows.getSectionPos(shape.sheetPos.rowBottom) + shape.sheetPos.rowBottomOffset);
				node.setBoundingBoxTo(box);
				node.setAngle(angle);
			}

			node._lastJSON = jsonShape;
			parentMap[shape.id] = node;
			itemMap[shape.id] = node;
			deleteMap[shape.id] = undefined;
			if (node instanceof StreamSheet) {
				// do not removed unsaved sub items
				const contentPane = node._subItems[0];
				deleteMap[contentPane.getId()] = undefined;
				for (let i = 0; i < 4; i += 1) {
					deleteMap[contentPane._subItems[i].getId()] = undefined;
				}
			} else if (shape.shape && shape.shape.source) {
				node.getShape().refresh();
			}
		});

		// remove deleted items
		Object.values(deleteMap).forEach(value => {
			if (value !== undefined && value.getParent() ) {
				value.getParent().removeItem(value);
				notify = true;
			}
		});

		if (notify) {
			NotificationCenter.getInstance().send(new JSG.Notification('SHAPE_NOTIFICATION', {}));
		}
	}

    getContainer(item, parent) {
		if (item instanceof StreamSheet) {
			return undefined;
		}

		let ws = parent;
		while (ws && !(ws instanceof StreamSheet)) {
			ws = ws.getParent();
		}

		return ws;
	}

	replaceTerm(formula, expression, index) {
		const newExpression = this.textToExpression(String(formula));
		const term = expression.getTerm();

		if (term && newExpression) {
			for (let i = term.params.length; i < index; i += 1) {
				term.params[i] = new NullTerm();
			}

			term.params[index] = newExpression.term;
			expression.correctFormula(this.model, true);
		}
	}
};

StreamSheet.createSelectionId = createSelectionId;

module.exports = StreamSheet;
